import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import './styles.scss'
import logo from 'src/images/ns_logo_wordmark.svg'
import arrowIcon from 'src/images/arrow_icon.svg'
import { ModalCloseIcon } from 'src/views/components/Icons/ModalCloseIcon'
import { getLocaleCodeFromUrl } from 'src/utils'
import { safeNavigate } from 'src/utils/safeNavigate'
import { marketSelectorData } from 'src/models/ContentStack/transform'

const REMEMBER_KEY = 'marketSelectorRemember'
const SELECTION_KEY = 'marketSelectorSelection'

const MarketSelector = forwardRef((props, ref) => {
  const [fullscreen, setFullscreen] = useState(true)
  const [show, setShow] = useState(false)
  const [remember, setRemember] = useState(false)
  const [languageData, setLanguageData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState({
    country: '',
    currency: '',
    lang: '',
  })
  const [expandedRegionMobile, setExpandedRegionMobile] = useState(null)

  let locale = getLocaleCodeFromUrl() || ''
  locale = locale?.split?.('_')?.reverse?.().join?.('-') || 'US-en'

  const [originalUrl, setOriginalUrl] = useState('')
  const getLanguageDisplayName = lang => {
    if (!lang) return ''
    const useFullName = languageData?.use_full_language_name === true
    return useFullName ? lang?.full_name || '' : lang?.general_name || ''
  }

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setOriginalUrl(window.location.href)
    }
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (show) {
        const handlePopState = event => {
          if (event.state?.modalOpen) {
            return
          } else {
            setShow(false)
          }
        }
        window.addEventListener('popstate', handlePopState)
        return () => {
          window.removeEventListener('popstate', handlePopState)
        }
      } else {
        if (window.location.pathname === '/region_selector') {
          window.history.pushState(null, '', originalUrl)
        }
      }
    }
  }, [show, originalUrl])

  useEffect(() => {
    setLoading(true)
    setError(null)

    const fetchData = async () => {
      try {
        const data = JSON.parse(JSON.stringify(props.regionSelector))
        if (!data || data?.error || data?.isEmptyResponse) {
          throw new Error('No valid data from ContentStack API')
        }
        const transformedData = marketSelectorData({ entry: data })
        if (transformedData?.markets?.length > 0) {
          setLanguageData(transformedData)
        } else {
          throw new Error('No markets data available from ContentStack')
        }
      } catch (error) {
        setError(error?.message)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [props.regionSelector])

  useEffect(() => {
    if (languageData?.markets?.length && !selectedLanguage?.country) {
      const [country, lang] = locale?.split?.('-') || []
      let foundCountry, foundLanguage, foundRegion
      for (const market of languageData.markets) {
        const matchingCountry = market?.countries?.find(
          c => c?.iso_code_2?.toUpperCase() === country?.toUpperCase()
        )
        if (matchingCountry) {
          const matchingLanguage = matchingCountry.languages?.find(
            l => l?.code?.toLowerCase() === lang?.toLowerCase()
          )
          if (matchingLanguage) {
            foundCountry = matchingCountry
            foundRegion = market?.region
            foundLanguage = matchingLanguage
            break
          }
        }
      }
      if (foundCountry && foundLanguage) {
        const displayName = getLanguageDisplayName(foundLanguage)
        setSelectedRegion(foundRegion)
        setSelectedLanguage({
          country: foundCountry?.name,
          currency: foundCountry?.currency,
          lang: displayName,
        })
      }
    }
  }, [languageData, locale])

  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBER_KEY)
    const selection = localStorage.getItem(SELECTION_KEY)
    if (remembered === 'true' && selection) {
      try {
        const parsed = JSON.parse(selection)
        setSelectedLanguage(parsed)
        setRemember(true)

        if (languageData?.markets) {
          const region = languageData?.markets?.find(market =>
            market?.countries?.some(
              country => country?.name === parsed?.country
            )
          )
          if (region) {
            setSelectedRegion(region?.region)
          }
        }
      } catch (e) {}
    }
  }, [languageData])

  useEffect(() => {
    localStorage.setItem(REMEMBER_KEY, remember)
    if (
      remember &&
      selectedLanguage?.country &&
      selectedLanguage?.currency &&
      selectedLanguage?.lang
    ) {
      localStorage.setItem(SELECTION_KEY, JSON.stringify(selectedLanguage))
    }
    if (!remember) {
      localStorage.removeItem(SELECTION_KEY)
    }
  }, [remember, selectedLanguage])

  function handleShow(breakpoint) {
    setFullscreen(breakpoint)
    setShow(true)
  }

  const handleClose = e => {
    if (e) e.stopPropagation()
    setShow(false)
  }

  const handleRegionKeyDown = (e, region, languages) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault()
      const firstLanguageElement = document.querySelector(
        `.market-country-languages [tabindex="0"]`
      )
      if (firstLanguageElement) {
        firstLanguageElement.focus()
      }
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      const tabs = Array.from(document.querySelectorAll('.market-region-tab'))
      const currentIndex = tabs.findIndex(tab => tab === e.target)
      const nextIndex =
        e.key === 'ArrowRight'
          ? (currentIndex + 1) % tabs.length
          : (currentIndex - 1 + tabs.length) % tabs.length
      tabs[nextIndex]?.focus()
      // Also update the selected region when navigating with arrows
      const nextRegion = regions[nextIndex]
      if (nextRegion) {
        setSelectedRegion(nextRegion)
      }
    } else if (
      e.key &&
      e.key.length === 1 &&
      ((e.key >= 'a' && e.key <= 'z') || (e.key >= 'A' && e.key <= 'Z'))
    ) {
      e.preventDefault()
      const allLanguages = document.querySelectorAll(
        '.market-country-languages [tabindex="0"]'
      )
      const matchingLanguage = Array.from(allLanguages).find(el =>
        el.textContent.toLowerCase().startsWith(e.key.toLowerCase())
      )
      if (matchingLanguage) {
        matchingLanguage.focus()
      }
    }
  }

  const handleLanguageKeyNav = (e, lang) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      const allLanguages = Array.from(
        document.querySelectorAll('.market-country-languages [tabindex="0"]')
      )
      const currentIndex = allLanguages.indexOf(e.target)

      if (currentIndex === 0 || currentIndex === -1) {
        // Go back to the active region tab
        const regionTab = document.querySelector('.market-region-tab.active')
        if (regionTab) {
          regionTab.focus()
        }
      } else {
        // Go to previous language
        const prevIndex = currentIndex - 1
        allLanguages[prevIndex]?.focus()
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      const allLanguages = Array.from(
        document.querySelectorAll('.market-country-languages [tabindex="0"]')
      )
      const currentIndex = allLanguages.indexOf(e.target)
      const nextIndex = (currentIndex + 1) % allLanguages.length
      allLanguages[nextIndex]?.focus()
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      const allLanguages = Array.from(
        document.querySelectorAll('.market-country-languages [tabindex="0"]')
      )
      const currentIndex = allLanguages.indexOf(e.target)
      const nextIndex =
        e.key === 'ArrowRight'
          ? (currentIndex + 1) % allLanguages.length
          : (currentIndex - 1 + allLanguages.length) % allLanguages.length
      allLanguages[nextIndex]?.focus()
    }
  }

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleShow,
    getSelectedLanguageLabel,
  }))

  const handleLanguageSelect = (
    countryName,
    currency,
    lang,
    general_name,
    iso_code_2
  ) => {
    const languageObj = { full_name: lang, general_name: general_name }
    const displayLanguage = getLanguageDisplayName(languageObj)

    setSelectedLanguage({
      country: countryName,
      currency,
      lang: displayLanguage,
    })
    setShow(false)
    const language = iso_code_2

    let COUNTRY = null
    for (const market of languageData?.markets || []) {
      const country = market?.countries?.find(c => c?.name === countryName)
      if (country?.iso_code_2) {
        COUNTRY = country?.iso_code_2?.toUpperCase()
        break
      }
    }

    if (language && COUNTRY && typeof window !== 'undefined') {
      const homeUrl = `${window.location.origin}/${COUNTRY.toLowerCase()}/${language}/`
      try {
        window.location.href = homeUrl
      } catch (navError) {
        window.location.href = homeUrl
      }
    }
  }

  const regions = languageData?.markets?.map(m => m?.region) || []
  const countries =
    languageData?.markets?.find(m => m?.region === selectedRegion)?.countries ||
    []

  function getSelectedLanguageLabel() {
    if (
      !selectedLanguage?.country ||
      !selectedLanguage?.currency ||
      !selectedLanguage?.lang
    ) {
      return ''
    }
    return `${selectedLanguage?.currency} | ${selectedLanguage?.lang}`
  }

  const handleRegionToggleMobile = region => {
    setExpandedRegionMobile(expandedRegionMobile === region ? null : region)
  }

  if (loading || error || !languageData || !languageData.markets?.length) {
    return null
  }

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 600
  const labels = languageData?.labels || {}

  const handleCountryRedirect = country => {
    const isoCode = country?.iso_code_2
    const defaultLang = country?.default_language_code
  
    const validCountry = languageData?.markets
      ?.flatMap(market => market.countries)
      ?.find(c => c.iso_code_2 === isoCode)
    const validLang = validCountry?.languages?.find(l => l.code === defaultLang)
  
    const isoCodePattern = /^[A-Z]{2}$/i
    const langPattern = /^[a-z]{2}$/i
  
    if (
      validCountry &&
      validLang &&
      typeof window !== 'undefined' &&
      isoCodePattern.test(isoCode) &&
      langPattern.test(defaultLang)
    ) {
      const countryUrl = `/${isoCode.toLowerCase()}/${defaultLang.toLowerCase()}/`
      safeNavigate(countryUrl, '/')
    }
  }

  return (
    <>
      <Modal
        show={show}
        fullscreen={fullscreen}
        onHide={handleClose}
        backdrop={isMobile ? false : true}
        keyboard={isMobile ? false : true}
        dialogClassName="market-modal-fullscreen"
        className="market-selector-modal">
        {isMobile ? (
          <div className="market-mobile-header">
            <div className="market-mobile-header-row">
              <div className="market-header-title-mobile">
                {labels?.selectYourMarket}
              </div>
              <div
                className="market-header-cancel-mobile"
                onClick={handleClose}
                tabIndex={0}
                role="button"
                aria-label={labels?.cancel}
                onKeyPress={e => {
                  if (e.key === 'Enter' || e.key === ' ') handleClose()
                }}>
                {labels?.cancel}
              </div>
            </div>
          </div>
        ) : (
          <div className="market-modal-header">
            <div className="market-header-left">
              <img
                src={logo}
                alt="Nu Skin logo"
                className="market-header-logo"
              />
            </div>
            <div className="market-header-center">
              <div className="market-header-title">
                {labels?.selectYourMarket}
              </div>
            </div>
            <div
              className="market-header-cancel"
              onClick={handleClose}
              tabIndex={0}
              role="button"
              aria-label={labels?.cancel}
              onKeyPress={e => {
                if (e.key === 'Enter' || e.key === ' ') handleClose()
              }}>
              <ModalCloseIcon className="market-header-cancel-icon" />
              <span className="market-header-cancel-text">
                {labels?.cancel}
              </span>
            </div>
          </div>
        )}
        <Modal.Body>
          {isMobile ? (
            <>
              <div
                className={`mobile-checkbox-row${remember ? ' active' : ''}`}>
                <Form.Check
                  type="checkbox"
                  id="remember-selection"
                  label={labels?.rememberMySelection}
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                />
              </div>

              {selectedLanguage?.country && (
                <div className="mobile-selected-language-section">
                  {(() => {
                    const selectedCountry = languageData?.markets
                      ?.flatMap(market => market?.countries || [])
                      ?.find(
                        country => country?.name === selectedLanguage?.country
                      )
                    return (selectedCountry?.languages || [])?.map(lang => {
                      const displayName = getLanguageDisplayName(lang)
                      const isSelected = selectedLanguage?.lang === displayName
                      return (
                        <div
                          key={lang?.code}
                          className={`mobile-current-selection${isSelected ? ' selected' : ''}`}
                          onClick={() =>
                            handleLanguageSelect(
                              selectedCountry?.name,
                              selectedCountry?.currency,
                              lang?.full_name,
                              lang?.general_name,
                              lang?.code
                            )
                          }>
                          <span className="mobile-selection-text">
                            {selectedCountry?.currency} | {displayName}
                          </span>
                          {isSelected && (
                            <span className="mobile-selection-checkmark">
                              ✓
                            </span>
                          )}
                        </div>
                      )
                    })
                  })()}
                </div>
              )}
              <div className="mobile-regions-container">
                {(regions || [])?.map(region => {
                  const regionCountries =
                    languageData?.markets?.find(m => m.region === region)
                      ?.countries || []
                  const isExpanded = expandedRegionMobile === region
                  const isActiveRegion = regionCountries?.some(
                    country => country?.name === selectedLanguage?.country
                  )
                  return (
                    <div key={region} className="mobile-region-section">
                      <div
                        className={`mobile-region-header${isExpanded ? ' expanded' : ''}${isActiveRegion ? ' active' : ''}`}
                        onClick={() => handleRegionToggleMobile(region)}
                        tabIndex={0}
                        role="button"
                        aria-label={region}
                        aria-expanded={isExpanded}
                        onKeyPress={e => {
                          if (e.key === 'Enter' || e.key === ' ')
                            handleRegionToggleMobile(region)
                        }}>
                        <span className="mobile-region-title">{region}</span>
                        <span
                          className={`mobile-region-arrow${isExpanded ? ' expanded' : ''}${isActiveRegion ? ' active' : ''}`}>
                          <img
                            src={arrowIcon}
                            alt="arrow"
                            className="arrow-icon"
                          />
                        </span>
                      </div>
                      {isExpanded && (
                        <div className="mobile-region-content">
                          {(regionCountries || [])?.map(country => (
                            <div
                              key={country?.name}
                              className="mobile-country-section">
                              <div className="mobile-country-header">
                                <span className="mobile-country-title">
                                  {country?.name}
                                </span>
                                <span className="mobile-country-arrow">→</span>
                              </div>
                              <div className="mobile-languages-list">
                                {(country?.languages || [])?.map(lang => {
                                  const displayName =
                                    getLanguageDisplayName(lang)
                                  const isSelected =
                                    selectedLanguage?.country ===
                                      country?.name &&
                                    selectedLanguage?.currency ===
                                      country?.currency &&
                                    selectedLanguage?.lang === displayName
                                  return (
                                    <div
                                      className={`mobile-language-item${isSelected ? ' selected' : ''}`}
                                      key={lang?.code}
                                      tabIndex={0}
                                      role="button"
                                      aria-label={displayName}
                                      aria-pressed={isSelected}
                                      onClick={() =>
                                        handleLanguageSelect(
                                          country?.name,
                                          country?.currency,
                                          lang?.full_name,
                                          lang?.general_name,
                                          lang?.code
                                        )
                                      }
                                      onKeyPress={e => {
                                        if (e.key === 'Enter' || e.key === ' ')
                                          handleLanguageSelect(
                                            country?.name,
                                            country?.currency,
                                            lang?.full_name,
                                            lang?.general_name,
                                            lang?.code
                                          )
                                      }}>
                                      <span className="mobile-language-name">
                                        {displayName}
                                      </span>
                                      {isSelected && (
                                        <span className="mobile-language-checkmark">
                                          ✓
                                        </span>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          ) : (
            // Desktop code changes
            <>
              <div className="market-header-checkbox-row">
                <Form.Check
                  type="checkbox"
                  id="remember-selection-desktop"
                  label={labels?.rememberMySelection}
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                />
              </div>
              <div
                className="market-region-tabs"
                role="tablist"
                aria-label="Market regions">
                {(regions || [])?.map((region, index) => (
                  <div
                    key={region}
                    className={`market-region-tab${selectedRegion === region ? ' active' : ''}`}
                    onClick={() => setSelectedRegion(region)}
                    onFocus={() => setSelectedRegion(region)}
                    tabIndex={0}
                    role="tab"
                    id={`region-tab-${index}`}
                    aria-selected={selectedRegion === region}
                    aria-controls={`region-panel-${index}`}
                    aria-label={`${region} region`}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        setSelectedRegion(region)
                      }
                      handleRegionKeyDown(e, region)
                    }}>
                    {region}
                  </div>
                ))}
              </div>
              <div className="market-countries-grid">
                <Row className="market-countries-grid-row">
                  {(countries || [])?.map(country => (
                    <Col
                      key={country?.name}
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      className="market-country-card">
                      <div
                        className="market-country-header"
                        onClick={() => handleCountryRedirect(country)}>
                        <span className="market-country-name">
                          {country?.name}
                        </span>
                        <span className="market-country-arrow">&rarr;</span>
                      </div>
                      <div className="market-country-languages">
                        {(country?.languages || [])?.map(lang => {
                          const displayName = getLanguageDisplayName(lang)
                          const isSelected =
                            selectedLanguage?.country === country?.name &&
                            selectedLanguage?.currency === country?.currency &&
                            selectedLanguage?.lang === displayName
                          return (
                            <span
                              className={`market-country-language${isSelected ? ' selected' : ''}`}
                              key={lang?.code}
                              tabIndex={0}
                              role="button"
                              aria-label={displayName}
                              aria-pressed={isSelected}
                              onClick={() =>
                                handleLanguageSelect(
                                  country?.name,
                                  country?.currency,
                                  lang?.full_name,
                                  lang?.general_name,
                                  lang?.code
                                )
                              }
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  handleLanguageSelect(
                                    country?.name,
                                    country?.currency,
                                    lang?.full_name,
                                    lang?.general_name,
                                    lang?.code
                                  )
                                }
                                handleLanguageKeyNav(e, lang)
                              }}>
                              {displayName}
                              {isSelected && !isMobile && (
                                <span
                                  className="language-selected-icon"
                                  aria-hidden="true">
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20">
                                    <circle
                                      cx="10"
                                      cy="10"
                                      r="10"
                                      fill="#222"
                                    />
                                    <polyline
                                      points="6,11 9,14 14,7"
                                      fill="none"
                                      stroke="#fff"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                              )}
                            </span>
                          )
                        })}
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
})

MarketSelector.propTypes = {
  regionSelector: PropTypes.object.isRequired,
}

export default MarketSelector
